import { createContext, useState } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { IntlProvider, load } from '@progress/kendo-react-intl';

import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';

import localCurrencyHe from 'cldr-numbers-full/main/he/currencies.json';
import numbersHe from 'cldr-numbers-full/main/he/numbers.json';
import caGregorianHe from 'cldr-dates-full/main/he/ca-gregorian.json';
import dateFieldsHe from 'cldr-dates-full/main/he/dateFields.json';

import esMessages from './kendoLocalization.json';
loadMessages(esMessages.he, 'he');

load(
    likelySubtags,
    currencyData,
    weekData,

    localCurrencyHe,
    numbersHe,
    caGregorianHe,
    dateFieldsHe
);

const ui_labels = {
    he: {
        dir: 'rtl',
        dimensions_textField: 'nameHe',

        he: 'עברית',
        ar: 'ערבית',
        en: 'אנגלית',
        ivritil: 'IvritIL',

        // Language + Workspace combo labels
        he_ofek: 'אופק - עברית',
        ar_ofek: 'אופק - ערבית',
        en_ofek: 'אופק - אנגלית',
        en_ivritil: 'IvritIL',
        he_myTestBox: 'MyTestBox',

        site_title: 'אדמין ניירובי',
        key: 'מפתח',

        save_error_title: 'שגיאה',
        save_error_message: 'הייתה שגיאה בניסיון לשמור נתונים.',
        delete_error_title: 'שגיאה',
        delete_error_message: 'הייתה שגיאה בניסיון למחוק נתונים.',
        loading_error_title: 'שגיאה',
        loading_error_message: 'הייתה שגיאה בניסיון למשוך נתונים מהשרת.',
        last_publication: 'עדכון אחרון',
        last_editor: 'עורך אחרון',
        updating: 'עדכון',

        pages: {
            books: 'ספרים',
            courses: 'קורסים',
            recommendations: 'המלצות',
            catalog: 'מאגר',
            teachersLearn: 'לומדים בקטנה',
        },

        user_permissions_popup: {
            title: 'הרשאות',
            message_part_1: 'רק לאדמין יש הרשאות להשתמש במערכת זו.',
            message_part_2: 'זמנית לא ניתן לעשות הזדהות מול מערכת זו. עבור ל ',
            message_part_3: ', כדי להיתחבר כאדמין ו אחרי זה חזור.',
            message_part_4: 'אם אתה כבר מחובר כאדמין, ',
            message_part_5: 'לחץ כאן',
            message_part_6: ' כדי לבדוק הרשאות שנית.',
        },

        menu: {
            pages: 'דפים',
            labels: 'ניהול לייבילים',
            edit_existing_items: 'עריכת פריטים קיימים',
            add_new_items: 'הוספת פריט חדש',
            repository: 'ניהול מאגר הפרטים',
            systemMessages: 'הודעות מערכת',
            systemMessages_elite: 'עילית',
            systemMessages_dominate: 'משתלטת',
            internal_pages: 'עמודים פנימיים',

            'page_home': 'דף ראשי',
            'page_recommendations': 'דף המלצות עבורך',
            'page_books': 'דף ספרים',
            'page_courses': 'דף קורסים',
            'page_tools': 'דף כלי יצירה',
            'page_teachersLearn': 'דף לומדים בקטנה',
            'page_sequences': 'רצפים',
            'page_my-studio': 'הסטודיו שלי',
            'page_my-content': 'תוכן שיצרתי',
            'page_learning-management': 'ניהול למידה',
            'page_online-meetings': 'מפגשים מקוונים',
            'page_micro-courses': 'מיקרו קורסים',
            'page_search': 'דף חיפוש',
            'page_catalog': 'דף מאגר התכנים',
            'page_my-favorites': 'דף מועדפים',
            'page_my-profile': 'דף אזור אישי',
        },

        list_editor: {
            item_remove_text: 'למחוק את הפריט?',
            item_remove_yes_btn: 'למחוק',
            item_remove_no_btn: 'ביטול',
            add_new_item_btn: 'הוספה'
        },

        unsaved_changes: {
            message: 'יש שינוים שלא נישמרו, האם להמשיך ללא שמירה?',
            continue_without_saving: 'המשך ללא שמירה',
            save: 'שמור',
        },

        thumbnailUploader: {
            choose_image: 'בחר תמונה או גרור אותה לכאן',
            select_one_image: 'יש לבחור תמונה אחד',
            max_file_size: 'גודל תמונה מקסימלי 5MB',
            unsupported_file_format: 'סוג הקובץ לא נתמך',
            hint: 'פורמטים נתמכים (png, jpeg, jpg). גודל תמונה מקסימלי 5MB. ',
            update_after_save: 'קישורים מתעדכנים רק אחרי שמירת פריט.',
            image_title_size_1: '666X239 - תצוגה מורחבת',
            image_title_size_2: '220X186 - רמה ראשונה',
        },

        imageUploader: {
            hint: 'פורמטים נתמכים (jpeg, jpg, png, gif). גודל תמונה מקסימלי 5MB. ',
            seo_hint: 'פורמטים נתמכים (jpeg, jpg, png). גודל תמונה מקסימלי 5MB. מומלץ לשים תמונה בגודל 500x500.',
        },

        videoUploader: {
            choose_video: 'בחר וידאו או גרור אותו לכאן',
            select_one_video: 'יש לבחור וידאו אחד',
            max_file_size: 'גודל וידאו מקסימלי 10MB',
            unsupported_file_format: 'סוג הקובץ לא נתמך',
            hint: 'פורמטים נתמכים (mp4, ogv, wmv, mov, webm, avi, mts, m2ts). גודל וידאו מקסימלי 10MB. '
        },

        animationUploader: {
            choose_animation: 'בחר אנימציה או גרור אותה לכאן',
            select_one_video: 'יש לבחור אנימציה אחת',
            max_file_size: 'גודל אנימציה מקסימלי 1',
            unsupported_file_format: 'סוג הקובץ לא נתמך',
            hint: 'פורמטים נתמכים (json). גודל אנימציה מקסימלי 5MB. '
        },

        editorImageUploader: {
            insert_image: 'הוסף תמונה',
            cancel: 'בטל',
            insert: 'הוסף',
            image: 'תמונה',
            web_address: 'קישור לתמונה',
            alternate_text: 'טקסט חלופי',
            title: 'כותרת',
            width: 'רוחב (px)',
            height: 'גובה (px)',
        },

        bannersManagement: {
            grid_title: 'מחסנית באנרים פרסונליים לעמודים פנימיים. ישולב בדפים: ספרים, קורסים, המלצות, מאגר, ליווי פדגוגי. בכל דף יוצג באנר ראנדומלית רק בדפים שיוגדרו החלפת באנר פר session פר דף - לא אותו דבר בדפים שונים כל עוד יש די באנרים.',
            new_item: 'באנר חדש',
            languages_textField: 'nameHe',
            languages_label: 'בחרו שפה',
            userType_label: 'בחר סוג משתמש',
            userTypes: {
                teacher: 'פרסונלי - מורה',
                coordinator: 'פרסונלי - רכז תקשוב',
                director: 'פרסונלי - מנהל',
            },

            searchResult: {
                num: 'מספר סידורי',
                id: 'ID',
                title: 'שם הבאנר',
                lastUpdatedDate: 'תאריך עדכון אחרון',
                isPublish: 'סטטוס פרסום',
                type: 'סוג באנר',
                types: {
                    TextImage_image: 'טקסט בצד תמונה',
                    TextImage_animate: 'טקסט בצד אנימציה',
                    TextImage_video: 'טקסט בצד וידאו',
                }
            },

            editor: {
                back_to_item_list: 'חזרה לרשימת הבאנרים',
                delete_item: 'מחק באנר',
                delete_message: 'אתה בטוח רוצה למחוק את הבאנר.',
                delete: 'מחק',
                cancel_delete: 'בטל מחיקה',
                add_pages: 'הצג בעמודים',
            }
        },

        repositoryManagement: {
            add_new_item: 'נא להזין קישור לפריט',
            disciplines_label: 'בחרו תחום',
            disciplines_textField: 'nameHe',
            disciplines_allDisciplines: 'כל התחומים',
            languages_label: 'בחרו שפה',
            languages_allLanguages: 'כל השפות',
            languages_textField: 'nameHe',
            itemType_textField: 'nameHe',
            itemSource_textField: 'nameHe',
            itemContext_textField: 'nameHe',
            ageGrade_textField: 'nameHe',
            new_item: 'פריט חדש',
            back_to_item_list: 'חזרה לרשימת הפריטים',
            delete_item: 'מחק פריט',
            singleDiscipline: 'תחום יחיד',
            multiDiscipline: 'רב-תחומי / בין-תחומי',
            allDisciplines: 'כל התחומים',
            lastUpdate: 'עדכון אחרון במאגר',
            added: 'התווסף למאגר',
            lastPublishedCatalog: 'פרסום אחרון במאגר',
            lastPublished: 'פרסום אחרון במקור',
            by: "על-ידי",
            minutes: "דקות",
            grade: 'כיתה',
            grades: 'כיתות',

            item: {
                id: 'מזהה פריט',
                isMobileSupported: 'האם נתמך במובייל',
                addedDate: 'תאריך הוספת הפריט למאגר',
                addedUser: 'התווסף למאגר על ידי',
                lastPublishedDate: 'תאריך פרסום אחרון של הפריט',
                lastCatalogPublishedDate: 'תאריך פרסום אחרון במאגר',
                lastUpdatedDate: ' תאריך עדכון אחרון במאגר',
                lastUpdatedUser: 'משתמש שעדכן אחרון',
                itemSource: 'מקור הפריט',
                itemContext: 'הקשר הפריט',
                externalContextId: 'מזהה ממאגר מקור',
                contextLanguage: 'שפה',
                contextURL: 'קישור לפריט',
                title: 'כותרת',
                titleHe: 'כותרת עברית',
                titleEn: 'כותרת אנגלית',
                titleAr: 'כותרת ערבית',
                subtitle: 'כותרת משנה',
                subtitleHe: 'כותרת משנה עברית',
                subtitleEn: 'כותרת משנה אנגלית',
                subtitleAr: 'כותרת משנה ערבית',
                summary: 'תקציר',
                summaryHe: 'תקציר בעברית',
                summaryEn: 'תקציר באנגלית',
                summaryAr: 'תקציר בערבית',
                duration: 'משך פעילות',
                isPublished: 'האם בפרסום?',
                doNotImport: 'בקשה למחיקת הפריט (בחירת אפשרות זו תשלח בקשה אוטומטית למחיקת הפריט לצוות הטכנולוגיה)',
                isNeedsPayment: 'בתשלום',
                itemImage: 'תמונה',
                totalNumOfQuestions: 'מספר שאלות כולל',
                numOfAutomaticQuestions: 'מספר שאלות אוטומטיות',
                additionalInfo: 'מידע נוסף',
                authors: 'מחברים',
                publishers: 'מו"ל',
                copyrightHolders: 'בעלי זכויות',
                audiences: 'קהל יעד',
                ageGrades: 'שכבת גיל',
                itemTypes: 'סוג פריט',
                activityType: 'סוג פעילות',
                subActivityType: 'תת-סוג פעילות',
                yearCyclesCard: 'זיקה למעגל השנה',
                yearCycleCategories: 'קטגוריות לסינון',
                yearCycles: 'חגים / מועדים / ימי ציון',
                disciplines: 'מקצוע',
                sectors: 'מגזרים',
                syllabus: 'תכנית הלימודים',
                limit: 'ניתן לבחור עד',
                syllabusLimit: 'תוכניות שונות',
                skillsLimit: 'מיומנויות שונות',
                skills: 'מיומנויות',
                selected: 'נבחרו',
                subjects: 'נושא',
                subSubjects: 'תת-נושא',
                details: 'פירוט',
                general: 'מיומנויות רוחביות/כלליות/חברתיות',
                lifeSkills: 'מיומנויות רגשיות-חברתיות',
                professional: 'מיומנויות מקצועיות',
                pedagogicValues: 'ערכים',
                internalComments: 'הערות',
                isMobileAdjustmentRequired: 'דורש התאמה למובייל',
                isImprovementRequired: 'דורש טיוב תוכן',
                isInformaticApproved: 'אושרה איכות המפתוח',
                thumbnailId: 'תמונות פריט',
                arTranslationRequired: 'נדרש תרגום לערבית',
                designApproved: 'אושר עיצובית',
                otherLangItemId: 'קישור לפריט תואם',
                otherLangHe: 'בעברית',
                otherLangAr: 'בערבית',
            },

            searchResult: {
                num: 'מספר סידורי',
                id: 'ID ניירובי',
                itemTypes: 'סוג פריט',
                itemSource: 'מקור הפריט',
                itemContext: 'הקשר הפריט',
                ageGrades: 'כיתה (שכבה)',
                title: 'שם הפריט',
                addedDate: 'תאריך כניסה לניירובי',
                lastUpdatedDate: 'תאריך עדכון אחרון',
                isPublished: 'סטטוס פרסום',
                lastCatalogPublishedDate: 'תאריך פרסום ניירובי',
                designApproved: 'אושר עיצובית',
                informaticApprovalDate: 'תאריך אישור מפתוח',
            },

            hints: {
                itemTypes: 'הסוג השולט/המאפיין את הפריט. ניתן לבחור עד 2 סוגים.',
                title: 'כותרת זהה לשם הפריט. עד 40 תווים. שימו לב: יוצגו רק 30 התווים הראשונים.',
                subtitle: 'כותרת אופרטיבית ולא ספרותית, נועדה לשרת את מערכות החיפוש וההמלצות, לא תוצג ללקוח.',
                activityType: 'יישום מומלץ של הפעילות בהקשר דידקטי',
                subActivityType: 'עבור ליווי פדגוגי (ננו-קורס)',
                summary: 'יש להגדיר בקצרה את הנושא ומטרות היחידה/השאלה עליה עונה הפריט. מומלץ לשלב מושגים חשובים, לשלב מילים מרכזיות ונרדפות.',
                chars: 'תווים.',
                publishNotReady: 'הפריט אינו מוכן לפרסום, יש למלא שדות חובה שלא הוזנו.',
                isPublishTrue: 'הפריט בפרסום.',
                publishedButNotValid: 'הפריט בפרסום אך אינו תקין.',
                readyForPublish: 'הפריט מוכן לפרסום.',
                yearCycles: 'התייחסות או זיקה לחגים, מועדים דתיים, ימי ציון לאומיים/אזרחיים ונקודות ציון על ציר שנה"ל',
                duration: 'מספר שעות הלימוד המתוכנן לקורס. ניתן להכניס 999 עבור קורס שנתי.',
                courseContent: 'עד שמונה משפטים קצרים מופרדים בירידת שורה (Enter). יוצגו באתר אחד מתחת לשני.',
                audiences: {
                    titles: [
                        'גננות',
                        'מורים',
                        'תלמידים',
                        'תלמידים עולים',
                        'תלמידים (מוסתר)'
                    ],
                    hints: [
                        `מיועד לגננות - חשוף לגננות + למורים`,
                        `מיועד למורים - חשוף למורים בלבד`,
                        `מיועד לתלמידים - חשוף לתלמידים + מורים`,
                        `מיועד לתלמידים עולים - חשוף לתלמידים עולים + מורים`,
                        `מיועד לתלמידים - חשוף למורים בלבד`
                    ]
                }
            },

            validators: {
                required: 'שדה חובה',
                requiredSkills: 'חובה לציין מיומנות מקצועית או מיומנות רוחבית',
                invalidUrl: 'כתובת לא תקינה',
                invalidCourseContentMaxRows: 'ניתן לכתוב עד שמונה משפטים קצרים בלבד',
                missingSyllabusSubjects: 'חסרים נושאים ל',
            },

            duration: {
                minutes60: 'שעה',
                minutes90: 'שעה וחצי',
                minutes120: 'שעתיים',
                minutes180: 'מעל שעתיים'
            },

            marketing: {
                title: 'מידע שיווקי',
                subTitle: 'תוכן שיווקי לרמה השלישית',
                courseVideoURL: 'קישור לסרטון שיווקי',
                courseMarketingURL: 'קישור לכפתור "לפרטים ולרכישה"',
                courseContent: 'מה נלמד בקורס',
                courseShowCreateEnvironmentButton: 'הצג כפתור שיוך',
            },

            nano: {
                title: 'ננו-קורס',
                subTitle: 'תוכן לרמה השלישית',
                nanoContent: 'מה יוצא לך מזה',
            },

            newItem: {
                new_item_catalog: 'הוסף פריט לקטלוג',
                intro: 'בשלב זה ניתן להוסיף פריטי תוכן מסוג LO, כותר או אתר WordPress. אלו התבניות התקינות:',
                kotar: 'כותר',
                itu: 'בין הצלצולים',
                presentation: 'פרזנטציה בריבוע',
                tarbutil: 'תרבות יהודית ישראלית',
                teachers: 'מורים מעבירים את זה הלאה',
                wp929: 'תנ"ך ביחד - 929',
                learning: 'Learning (Hendrx)',
                botikra: 'Botikra',
            }
        },

        labelsManagement: {
            new_label: 'רשומה חדשה',
            add_new_label: 'הוסף רשומה',
            cancel: 'בטל',
            new_label_error_empty_key: 'מפתח ריק',
            new_label_error_existing_key: 'מפתח קיים',
            new_label_error_max_key_size: 'גודל מקסימלי של מפתח 200',
        },

        systemMessagesManagement: {
            title_elite: 'ההודעה תופיע בראש הדף מתחת להאדר',
            title_dominate: 'ההודעה תופיע באמצע העמוד על רקע מוחשך עם איקס סגירה',
            on: 'הודעה מופעלת',
            off: 'הודעה כבויה',
            input_title: 'הזן כותרת',
            input_text: 'הזן טקסט',
            input_link: 'הזן קישור',
            input_popup: 'הזן תוכן של פופ אפ',
            message_click: 'לחיצה על הודעה',
            message_click_none: 'ללא פעולה',
            message_click_blank: 'לפתוח קישור (בחלון חדש)',
            message_click_top: 'לפתוח קישור (באותו חלון)',
            message_click_popup: 'לפתוח פופ אפ',
            show_button: 'הצג כפתור פעולה',
            show_hide_message_button: 'הצג אפשרות הסתרה',
            input_button_text: 'הזן טקסט לכפתור',
            button_click: 'לחיצה על כפתור',
            button_click_none: 'סגירת הודעה',
            button_click_blank: 'סגירת הודעה ו פתיחת קישור (בחלון חדש)',
            button_click_top: 'סגירת הודעה ו פתיחת קישור (באותו חלון)',
            schedule: 'הוספת תאריך ושעה',
            from: 'מ',
            to: 'עד',
            user_type_title: 'להציג הודעה עבור',
            user_type_teacher: 'מורה',
            user_type_guest: 'אורח',
            user_type_everyone: 'כל אחד',
            target_device_title: 'להציג הודעה עבור מכשיר',
            target_device_desktop: 'דסקטופ',
            target_device_mobile: 'מובייל',
            target_device_all: 'בכל המכשירים',
        },

        pagesManagement: {
            content: 'תוכן',
            seo: 'SEO',
            menu_titles: {
                unidentified_page: 'דף לא מזוהה',
                identified_page: 'דף מזוהה',
                tools_page: 'דף כלי יצירה',
                teachersLearn_page: 'דף לומדים בקטנה',
            },
            menu: {
                home: {
                    quick_links: 'קישורים מהירים',
                    hero_marketing_banner: 'הירו-באנר/טריפל',
                    hero_marketing_banner_general: 'הירו-באנר/טריפל',
                    hero_video: 'הירו-וידאו',
                    what_interests_you: 'מה מעניין אותך',
                    content_line: 'שורת תכנים',
                    text_image_banner: 'טקסט לצד מדיה',
                    text_media_banner_personal: 'טקסט לצד מדיה',
                    text_media_banner_personal_title: '(יאופיינו לפי שכבה מקצוע ומגזר). יוצגו למורה עד 3  באנרים מסוג זה בעמוד. במידה ויש יותר מ-3 מותאמים למורה נתון, יוחלפו כל session  למורה המזוהה.',
                    general_banner: 'באנר רגיל',
                    general_banner_personal: 'באנר רגיל',
                    test: 'test',
                },
                books: {
                    content_line: 'שורת תכנים',
                    text_image_banner: 'טקסט לצד מדיה',
                    hero_marketing_banner: 'הירו - באנר/טריפל',
                    video_banner: 'באנר וידאו',
                },
                courses: {
                    content_line: 'שורת תכנים',
                    text_image_banner: 'טקסט לצד מדיה',
                    hero_marketing_banner: 'הירו - באנר/טריפל',
                    video_banner: 'באנר וידאו',
                },
                tools: {
                    text_image_banner: 'טקסט לצד מדיה',
                    marketing_banner: ' וידאו/טריפל',
                    recommended_sites: 'אתרים מומלצים',
                },
                teachersLearn: {
                    text_image_banner: 'טקסט לצד מדיה',
                    video_banner: 'באנר וידאו',
                    content_line: 'שורת תכנים',
                }
            },
            forms: {
                show_for_unidentified_only: 'יופיע רק בדף הבית הלא מזוהה',
                show_for_identified_only: 'יופיע רק בדף הבית  המזוהה',
                show_both_identified_and_unidentified: 'יופיע בדף הבית הלא מזוהה והמזוהה',
                item: 'פריט',
                add_button_text: 'הזן טקסט לכפתור',
                add_link: 'הזן לינק',
                open_top: 'פתח באותו חלון',
                open_blank: 'פתח בחלון נפרד',
                for_external_links: 'מיועד לקישורים חיצוניים',
                link_type: 'סוג לינק',
                show_icon: 'הצג אייקון',
                old_site: 'אתר ישן',
                show: 'הצג',
                object_name: 'objectName for UI usage events',
                general: 'כללי',
                personal: 'פרסונלי',
                dedicated: 'ייעודי',
                hero: 'הירו',
                triple: 'טריפל',
                none: 'ללא',
                banner: 'באנר',
                add_pc_banner: 'הזן באנר לדסקטופ',
                add_mobile_banner: 'הזן באנר למובייל',
                add_pc_image: 'הזן תמונה לדסקטופ',
                add_mobile_image: 'הזן תמונה   למובייל',
                add_pc_animation: 'הזן json אנימציה לדסקטופ',
                add_mobile_animation: 'הזן json אנימציה למובייל',
                add_accessibility_title: 'הזן כותרת נגישות',
                banner_short_description: 'תיאור קצר של הבאנר',
                must_add_3_triple_items: 'חובה להזין את שלושת רכיבי הטריפל באופן זהה',
                add_triple_title: 'הזן כותרת לטריפל',
                add_title: 'הזן כותרת',
                add_text: 'הזן טקסט',
                upload_short_file: 'העלה קובץ מקוצר',
                upload_full_file: 'העלה קובץ מלא',
                add_youtube_link: 'הזן קישור לסרטון ב youtube',
                line_location: 'מיקום שורה בעמוד',
                top: 'עליון',
                bottom: 'תחתון',
                add_name: 'הזן שם',
                wont_be_in_site: 'לא יוצג באתר',
                location: 'מיקום',
                unidentified_page: 'בדף לא מזוהה',
                identified_and_unidentified_page: 'בדף לא מזוהה ומזוהה',
                identified_page: 'בדף מזוהה',
                status: 'סטטוס',
                must: 'חובה',
                status_must: 'סטטוס חובה יעניק עדיפות לבאנר ללא סימון בתצוגה במצב שיש מעל 3 באנרים',
                add_item_id: 'הזן מזהה פריט',
                add_item: 'הוסף פריט',
                delete_item: 'האם ברצונך למחוק פריט זה?',
                confirm: 'אישור',
                add_new_item: 'הוסף פריט נוסף',
                warning: 'הקישור שהוגדר אינו חוקי או שהפריט כבר קיים במאגר אופק או אינו בפרסום במאגר המקורי',
                added_successfully: 'הפריט נקלט בהצלחה',
                added_successfully_edit: 'עבור לעריכת הפריט',
                delete: 'מחק',
                cancel: 'בטל',
                or: 'או',
                textImage: 'טקסט לצד תמונה',
                action_button: 'כפתור פעולה',
                with_button: 'עם כפתור',
                without_button: 'ללא כפתור',
                required: 'שדה חובה',
                required_image_or_animation: 'חובה להזין תמונה או אנימציה',

                background_color: 'צבע רקע',
                set_background_color_false: 'ללא צבע רקע',
                set_background_color_true: 'עם צבע רקע',

                publish_title: 'האם בפרסום?',
                publish_not_ready: 'הפריט אינו מוכן לפרסום, יש למלא שדות חובה שלא הוזנו',
                publish_ready: 'הפריט מוכן לפרסום',
                is_publish: 'הפריט בפרסום',

                hero_marketing_banner: 'באנר אחד יוצג רנדומלית מאלה שבפרסום. לפחות אחד חייב להיות בפרסום.',
            },
            swimlineContent: {
                add_title: 'הזן כותרת לשורת התוכן',
                line_location: 'מיקום שורה בעמוד',
                top: 'עליון',
                bottom: 'תחתון',
                addNewItemBtnText: 'הוסף פריט תוכן לשורה',
                item: 'פריט',
                add_item_title: 'שם הפריט',
                add_item_id: 'הזן מזהה פריט',
                required: 'שדה חובה',

                swimline: 'שורת תכנים',
                swimline_title_books_page_identified_and_unidentified: 'יופיע בדף הספרים הלא מזוהה והמזוהה (אותו אחד)',
                swimline_title_home_page_identified_and_unidentified: 'יופיעו בעמוד הבית הלא מזוהה והמזוהה (אותו אחד)',
                swimline_title_courses_page_identified_and_unidentified: 'יופיעו בעמוד הקורסים הלא מזוהה והמזוהה (אותו אחד)',
            },
            swimlineListContent: {
                item: 'שורת תכנים',
                addNewItemBtnText: 'הוסף שורת תכנים',
                publish_title: 'האם לפרסם שורת תכנים?',
                is_publish: 'שורת תכנים בפרסום',
                publish_ready: 'שורת תכנים מוכנה לפרסום',
                swimline_list: 'שורות תכנים',
                swimline_list_title_home_page_identified_and_unidentified: 'יופיעו בעמוד הבית הלא מזוהה והמזוהה (אותו אחד)',
                swimline_list_title_books_page_identified_and_unidentified: 'יופיע בדף הספרים הלא מזוהה',
                swimline_list_title_courses_page_identified_and_unidentified: 'יופיעו בעמוד הקורסים הלא מזוהה',
            },
            textMediaBanner: {
                banner: 'באנר',
                addNewItemBtnText: 'הוסף באנר',

                add_name: 'הזן שם (לא יוצג באתר)',
                add_title: 'הזן כותרת',
                add_text: 'הזן טקסט',
                add_video_poster: 'העלה תמונת פתיח  וידאו לדסקטופ',
                add_video_poster_mobile: 'העלה תמונת פתיח  וידאו למובייל',
                display_type: 'תצוגה',
                display_type_image: 'טקסט בצד תמונה',
                display_type_animate: 'טקסט בצד אנימציה',
                display_type_video: 'טקסט בצד וידאו',

                text_media: 'טקסט בצד מדיה',
                text_media_title_books_page_unidentified: 'חובה 3 באנרים כלליים בפרסום של טקסט לצד תמונה\\וידאו\\אנימציה.',
                text_media_title_courses_page_unidentified: 'חובה 3 באנרים כלליים בפרסום של טקסט לצד תמונה\\וידאו\\אנימציה.',
                text_media_title_tools_page: "תצוגת הטקסט בדף כלי יצירה מוגבלת ל 125 תווים! אח'כ יופיע לינק 'גלו לי עוד', לחיצה תוביל לרמה שלישית",

                add_age_grades: 'שכבת גיל',
                add_disciplines: 'מקצוע',
                add_sectors: 'מגזרים',
            },
            marketingBanner: {
                banner: 'באנר',
                addNewItemBtnText: 'הוסף באנר',
                banner_type: 'סוג של הבאנר',
                banner_type_hero: 'הירו',
                banner_type_triple: 'טריפל',
                banner_type_video: 'וידאו',
                add_name: 'שם הבאנר',
                display_type: 'תצוגה',
                display_type_image: 'תמונה',
                display_type_animate: 'אנימציה',

                triple_item_title: 'רכיב',
                triple_item_missing_content: 'חסר תוכן',
                addNewTripleItemBtnText: 'הוסף רכיב',

                marketing_banner: 'הירו באנר / טריפל',
                marketing_banner_video_triple: 'וידאו/טריפל',
                marketing_banner_title_books_page_unidentified: 'באנר אחד יוצג  רנדומלית  מאלה שבפרסום. לפחות אחד חייב להיות בפרסום.',
                marketing_banner_title_courses_page_unidentified: 'באנר אחד יוצג  רנדומלית  מאלה שבפרסום. לפחות אחד חייב להיות בפרסום.',
                marketing_banner_title_tools_page: 'בדף כלי יצירה ניתן להציג באנר אחד מסוג וידאו או טריפל',
            },
            linkList: {
                banner: 'באנר',
                addNewItemBtnText: 'הוסף באנר',
                banner_type: 'סוג של הבאנר',
                banner_type_hero: 'הירו',
                banner_type_triple: 'טריפל',
                banner_type_video: 'וידאו',
                display_type: 'תצוגה',
                display_type_image: 'תמונה',
                display_type_animate: 'אנימציה',

                triple_item_title: 'רכיב',
                triple_item_missing_content: 'חסר תוכן',
                addNewTripleItemBtnText: 'הוסף רכיב',

                marketing_banner: 'הירו באנר / טריפל',
                marketing_banner_video_triple: 'וידאו/טריפל',
                marketing_banner_title_books_page_unidentified: 'באנר אחד יוצג  רנדומלית  מאלה שבפרסום. לפחות אחד חייב להיות בפרסום.',
                marketing_banner_title_courses_page_unidentified: 'באנר אחד יוצג  רנדומלית  מאלה שבפרסום. לפחות אחד חייב להיות בפרסום.',
                marketing_banner_title_tools_page: 'בדף כלי יצירה ניתן להציג באנר אחד מסוג וידאו או טריפל',
                add_link: 'הזן קישור לאתר',
                add_image: 'הוסף תמונת לוגו של האתר',
                add_name: 'הזן את שם האתר',
                add_disciplines: 'הזן את מקצוע האתר',
            },
            videoBanner: {
                video: 'וידאו',
                addNewItemBtnText: 'הוסף וידאו',
                add_title: 'הזן כותרת',
                add_video_title: 'הזן כותרת לוידאו',
                add_video_poster: 'העלה תמונת פתיח  וידאו לדסקטופ',
                add_video_poster_mobile: 'העלה תמונת פתיח  וידאו למובייל'
            },
            SEO: {
                title: 'כותרת העמוד',
                description: 'תיאור העמוד',
                keyWords: 'מילות מפתח (לטובת מנועי חיפוש)',
                keyWords_placeholder: 'הפרד מילות מפתח ב ,',
                image: 'הוסף תמונה של עמוד ( לטובת שיתוף ו מנועי חיפוש)',

                message_home_page: 'SEO של דף הבית',
                message_books_page: 'SEO של דף הספרים',
                message_courses_page: 'SEO של דף הקורסים',
            }
        }
    }
}

export const LocalizationContext = createContext();

export const AppLocalizationProvider = (props) => {
    const [localization, _setLocalization] = useState({
        ui_language: 'he',
        labels: ui_labels.he,
        content_language: 'he_ofek', // Default to first combo ID (he + ofek)
        show_content_language: false
    });
    const setLocalization = (newState) => {
        var state = { ...localization, ...newState };
        state.labels = ui_labels[state.ui_language];
        _setLocalization(state);
    };
    return <LocalizationContext.Provider value={[localization, setLocalization]}>
        <LocalizationProvider language={localization.ui_language}>
            <IntlProvider locale={localization.ui_language}>
                {props.children}
            </IntlProvider>
        </LocalizationProvider>
    </LocalizationContext.Provider>
};